import React, { useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import PresignedImage from '../common/PresignedImage';
import PresignedAudio from '../common/PresignedAudio';
import FileTypeIcon from '../common/FileTypeIcon';
import {
  formatAudioDuration,
  formatFileSize,
  getFileExtensionLabel,
  isDisplayableImage,
  canPreviewInApp,
  isRecordedVoiceNote,
} from '../../utils/fileUtils';
import { isAudioAttachment } from './TimelineFileCard';

const AttachmentsListModal = ({
  attachments = [],
  onClose,
  onViewImage,
  onDownload,
  onRemove,
}) => {
  const imageAttachments = useMemo(
    () => attachments.filter(isDisplayableImage),
    [attachments]
  );
  const fileAttachments = useMemo(
    () => attachments.filter((a) => !isDisplayableImage(a)),
    [attachments]
  );

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [onClose]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const openGalleryAt = (attachment) => {
    const gallery = attachments.filter((item) => canPreviewInApp(item));
    const imageIndex = gallery.findIndex(
      (a) => (a.id && a.id === attachment.id) || a.fileUrl === attachment.fileUrl
    );
    onViewImage?.(gallery, imageIndex >= 0 ? imageIndex : 0);
  };

  const handleFileActivate = (attachment) => {
    if (canPreviewInApp(attachment)) {
      openGalleryAt(attachment);
      return;
    }
    onDownload?.(attachment);
  };

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 bg-black/60 z-[1000] flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={handleBackdropClick}
    >
      <div
        className="bg-white dark:bg-gray-900 w-full sm:max-w-lg sm:rounded-xl rounded-t-xl shadow-xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700 shrink-0">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
            {attachments.length} attachment{attachments.length !== 1 ? 's' : ''}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="overflow-y-auto flex-1">
          {/* Image previews grid */}
          {imageAttachments.length > 0 && (
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-3">
                Images ({imageAttachments.length})
              </p>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {imageAttachments.map((attachment, idx) => (
                  <button
                    key={attachment.id || attachment.fileUrl || idx}
                    type="button"
                    onClick={() => openGalleryAt(attachment)}
                    className="relative aspect-square rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-800 hover:opacity-90 transition-opacity focus:ring-2 focus:ring-primary-500"
                    title={attachment.fileName}
                  >
                    <PresignedImage
                      fileKey={attachment.fileUrl}
                      alt={attachment.fileName}
                      originalName={attachment.fileName}
                      className="w-full h-full object-cover"
                      loadingClassName="w-full h-full bg-gray-200 dark:bg-gray-700 animate-pulse"
                    />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Non-image files */}
          {fileAttachments.length > 0 && (
            <ul className="py-2">
              {fileAttachments.length > 0 && imageAttachments.length > 0 && (
                <li className="px-4 pb-2">
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
                    Files ({fileAttachments.length})
                  </p>
                </li>
              )}
              {fileAttachments.map((attachment, index) => {
                const ext = getFileExtensionLabel(attachment.fileName);
                const isAudio = isAudioAttachment(attachment);
                const isVoiceNote = isRecordedVoiceNote(attachment);
                const originalIndex = attachments.indexOf(attachment);

                return (
                  <li key={attachment.id || index}>
                    <div className="w-full flex flex-col gap-2 px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                      <div className="flex items-center gap-3">
                      <div
                        className="flex items-center gap-3 min-w-0 flex-1 cursor-pointer"
                        onClick={() => handleFileActivate(attachment)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleFileActivate(attachment);
                          }
                        }}
                      >
                        <FileTypeIcon
                          attachment={attachment}
                          kind={isAudio ? 'audio' : undefined}
                          size="lg"
                          className="flex-shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {isVoiceNote ? 'Voice note' : attachment.fileName}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {[
                              isAudio && !isVoiceNote ? formatAudioDuration(attachment.durationSeconds) : '',
                              formatFileSize(attachment.fileSize),
                              isVoiceNote ? '' : ext,
                            ].filter(Boolean).join(' · ')}
                          </p>
                        </div>
                      </div>
                        {onDownload && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              onDownload(attachment);
                            }}
                            className="flex-shrink-0 p-2 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
                            title="Download"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                            </svg>
                          </button>
                        )}
                        {onRemove && (
                          <button
                            type="button"
                            onClick={() => onRemove(originalIndex)}
                            className="flex-shrink-0 p-2 rounded-lg text-gray-500 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/30"
                            title="Remove"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        )}
                      </div>
                      {isAudio && attachment.fileUrl && (
                        <PresignedAudio
                          fileKey={attachment.fileUrl}
                          className="w-full"
                          durationSeconds={attachment.durationSeconds}
                        />
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AttachmentsListModal;
